import {
  PROFILE_COUNTED_KINDS,
  PROFILE_MAIN_VARIANT,
  type ArtistProfileKind,
  type ArtistProfileResponse,
  type ArtistProfileVariant,
  type SerializedProfileAsset,
} from 'fym-shared/src/music24/artistProfile';

/**
 * The state of one profile image (design artist-profile a1–a3):
 * - `notMade`: no version yet.
 * - `making`: the newest version is queued or rendering; the card keeps the old file.
 * - `failed`: the newest version failed; the card keeps the old file and shows the error.
 * - `ready`: the current version has a file.
 */
export type ProfileSlotState = 'notMade' | 'making' | 'failed' | 'ready';

export interface ProfileSlot {
  kind: ArtistProfileKind;
  variant: ArtistProfileVariant;
  state: ProfileSlotState;
  /** The version that the card shows (the newest ready one), or null. */
  current: SerializedProfileAsset | null;
  /** The newest version when it is working or failed, else null. */
  working: SerializedProfileAsset | null;
  failed: SerializedProfileAsset | null;
}

type ProfileAssets = ArtistProfileResponse['assets'];

function isWorking(asset: SerializedProfileAsset | null): boolean {
  return asset !== null && (asset.status === 'queued' || asset.status === 'rendering');
}

/** The slot of one kind and variant from the profile answer. A missing entry is `notMade`. Pure. */
export function profileSlotOf(
  assets: ProfileAssets | undefined,
  kind: ArtistProfileKind,
  variant: ArtistProfileVariant = PROFILE_MAIN_VARIANT
): ProfileSlot {
  const entry = (assets ?? []).find((one) => one.kind === kind && one.variant === variant);
  const current = entry?.current?.status === 'ready' ? entry.current : null;
  const newest = entry?.newest ?? null;
  const working = isWorking(newest) ? newest : null;
  const failed = newest?.status === 'failed' ? newest : null;
  let state: ProfileSlotState = 'notMade';
  if (working !== null) {
    state = 'making';
  } else if (failed !== null) {
    state = 'failed';
  } else if (current !== null) {
    state = 'ready';
  }
  return { kind, variant, state, current, working, failed };
}

/** The slots of a kind, in the order of its variants (the three press crops). Pure. */
export function profileSlotsOf(
  assets: ProfileAssets | undefined,
  kind: ArtistProfileKind,
  variants: readonly ArtistProfileVariant[]
): ProfileSlot[] {
  return variants.map((variant) => profileSlotOf(assets, kind, variant));
}

/** True when a kind has a ready file in any variant. Pure. */
export function kindIsReady(assets: ProfileAssets | undefined, kind: ArtistProfileKind): boolean {
  return (assets ?? []).some((one) => one.kind === kind && one.current?.status === 'ready');
}

/** "N of 6 ready" of the Studio entry (design c3): the ready kinds; the press photo counts once, the bio not. Pure. */
export function profileReadyCount(assets: ProfileAssets | undefined): { ready: number; total: number } {
  return {
    ready: PROFILE_COUNTED_KINDS.filter((kind) => kindIsReady(assets, kind)).length,
    total: PROFILE_COUNTED_KINDS.length,
  };
}

/** True when nothing is made yet: the empty state of the page shows (design a2). Pure. */
export function profileIsEmpty(assets: ProfileAssets | undefined): boolean {
  return (assets ?? []).every((one) => one.current === null && one.newest === null);
}

/** True when the zip has a file to hold ("Download all" is off while nothing is ready). Pure. */
export function profileHasReadyFile(assets: ProfileAssets | undefined): boolean {
  return (assets ?? []).some((one) => one.current?.status === 'ready' && one.current.fileRemoved !== true);
}

/**
 * True while the server still works on something the page waits for: a version that is queued or
 * rendering, or an upload to Spotify that is sending. The page reads the profile again while it is. Pure.
 */
export function profileIsBusy(assets: ProfileAssets | undefined): boolean {
  return (assets ?? []).some(
    (one) =>
      isWorking(one.newest) || one.current?.spotify?.state === 'sending' || one.newest?.spotify?.state === 'sending'
  );
}
